import * as mobxReact from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { IEventDetailsDTO } from '../../../../../../api/events.dao';
import { withViewModel } from '../../../../../../infrastructure/components/with-view-model';
import { ISubWindowFormMethods } from '../../../manage-event.viewmodel';
import { OrganizationRole } from '../../../../../../modules/connections/types';
import { CommonPopupBodyFooterLayout } from '../../../../../global/common-popup/common-popup-body-footer-layout';
import OptionsButton from '../../../../../global/options-button/options-button';
import { DefaultCloseSubWindowButton } from '../../../../../global/sub-window/common/common-close-sub-window-buttons';
import { Permission } from '../../../../../../api/roles.dao';
import { hasPermission } from '../../../../../../infrastructure/utils/has-permissions';
import '../../connections.scss';
import { ConnectionType, IConnectionDetailsDTO, IMeetingConnectionsDTO, onChangeConnectionEntity } from '../../../../../../api/connections.dao';
import { UniqueJoiningLinkFormVM } from './unique-joining-link-modal.viewmodel';
import { UnassignedLinkList } from './unassigned-link-list/unassigned-link-list';
import { ParticipantConnectionList } from '../../_connection_participants/participant-connection-list';
import { applyText, cancelText, MORE_BUTTON_TEXT } from '../../../../../../constants/common-labels.constants';
import { MeetingConnectionsAggregateRoot } from '../../../../../../modules/connections/meeting/meeting-connections.aggregate-root';
import { ChildConnectionsViewModel } from '../child-connections/child-connections.viewmodel';

interface IProps {
    vm: UniqueJoiningLinkFormVM;
    intl: IntlShape;
}

const UniqueJoiningLinkModal = injectIntl(mobxReact.observer((props: IProps) => {
    // Check this functionaluty
    // eslint-disable-next-line no-unused-vars
    const [assigningConnection, setAssigningConnection] = useState<boolean>(false);
    const { intl, vm: formVM } = props;
    const formData = formVM.dataVM;
    const knovioReplayConnection = formVM.dataVM.getKnovioReplayConnection();
    const disabledDropDownConnection = !!knovioReplayConnection;

    useEffect(() => {
        formVM.updateUniqueJoiningLinkForm(() => {
            setAssigningConnection(prev => !prev);
        });
    }, []);

    if (!formData) return;

    const attendeesList = useMemo(() => {
        return formData.getOrganizationsListWithOrgRole();
    }, [
        formData.getOrganizationAttendees()
            .reduce((total, cur) => total + cur.attendees.length, 0)
    ]);

    const webinarRegistrantsCount =
        formVM.dataVM.getOrganizationsByRole(OrganizationRole.Registrants)
            .reduce((acc, curr) => acc + curr.getEntities().length, 0);

    const assignedConnectionsIds = formData.getOrganizationsList().reduce((ids, organization) => {
            ids.push(...organization.attendees.map(att => att.meetingConnectionId));
            return ids;
        }, []).filter(Boolean)
    ;

    const meetingConnections = [
        ...formData.getMeetingConnectionList()
    ];

    const unassignedMeetingConnections = meetingConnections.filter(el =>
        el.type === ConnectionType.UniqueJoiningLink && !assignedConnectionsIds.includes(el.meetingConnectionId)
    );

    const meetingConnectionOptions = [
        hasPermission(Permission.CONNECTIONS_MANAGEMENT) && {
            label: intl.formatMessage({
                id: 'oe.meeting.deleteAllUniqueJoiningLinks',
                defaultMessage: 'Delete All Unique Joining Links',
                description: 'Label text'
            }),
            action: formVM.handleDeleteUJL,
            disabled: formVM.shouldDisableDeleteUJLButton()
        },
        hasPermission(Permission.UPDATE_MEETING) && {
            label: intl.formatMessage({
                id: 'oe.meeting.editMeeting',
                defaultMessage: 'Edit Meeting',
                description: 'Label text'
            }),
            action: formVM.editMeeting,
        },
        hasPermission(Permission.RUN_MEETING) && {
            label: intl.formatMessage({
                id: 'oe.meeting.runMeeting',
                defaultMessage: 'Run Meeting',
                description: 'Label text'
            }),
            action: formVM.runMeeting,
        }
    ].filter(Boolean);

    const getConnectionsFormBody = (): JSX.Element => {
        return (
            <form className='oe-form meeting-registrants-form'>
                <div className='oe-form__body'>
                    <ParticipantConnectionList
                        key={OrganizationRole.Registrants}
                        roleName={intl.formatMessage({id: 'oe.input.registrants', description: 'Input label', defaultMessage: 'Registrants'})}
                        role={OrganizationRole.Registrants}
                        entity={formData}
                        attendees={[]}
                        isRoleRegistrant={true}
                        webinarRegistrantsCount={webinarRegistrantsCount}
                        editConnection={formVM.handleEditConnection}
                        meetingConnections={formData.getMeetingConnectionList()}
                        filteredMeetingConnections={formData.getFilteredMeetingConnectionList(ConnectionType.UniqueJoiningLink)}
                        connectionGroups={formData.getMeetingConnectionGroupList()}
                        onChangeMeetingConnection={formVM.onChangeMeetingConnection}
                        knovioReplayConnection={knovioReplayConnection}
                        disabledDropDownConnection={disabledDropDownConnection}
                        connectionVersion={formData.getProps().connectionVersion}
                        handleTrackChildCopyBtn={formVM.trackCopyButtonMixPanel}
                        childConnectionsVM={formVM.childConnectionsVM}
                    />
                </div>

                {!!unassignedMeetingConnections.length &&
                    <div className='oe-form__body'>
                        <UnassignedLinkList
                            formData={formData}
                            unassignedMeetingConnections={unassignedMeetingConnections}
                            onChangeMeetingConnection={formVM.onChangeMeetingConnection}
                            attendeesList={attendeesList}
                            setAssigningConnection={setAssigningConnection}
                            editConnection={formVM.handleEditConnection}
                            knovioReplayConnection={knovioReplayConnection}
                        />
                    </div>
                }
            </form>
        );
    };

    return (
        <CommonPopupBodyFooterLayout
            body={getConnectionsFormBody()}
            footer={(
                <>
                    {formVM.isSaving() && (
                        <span className='oe-popup-layout__footer-status-area'>
                            <FormattedMessage
                                id='oe.connections.connectionDataIsBeingSaved'
                                description='Preloader message text'
                                defaultMessage='Connection data is being saved, please wait{suffix}'
                                values={{
                                    suffix: '...'
                                }}
                            />
                        </span>
                    )}
                    {!formVM.getIsFormDataReady() && (
                        <span className='oe-popup-layout__footer-status-area'>
                            <FormattedMessage
                                id='oe.connections.connectionDataIsLoading'
                                description='Preloader message text'
                                defaultMessage='Connection data is loading, please wait{suffix}'
                                values={{
                                    suffix: '...'
                                }}
                            />
                        </span>
                    )}
                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultCloseSubWindowButton
                            text={applyText()}
                            modifier={['fat', 'confirm-safe']}
                            onClick={formVM.handleSubmitAction}
                            disabled={!formVM.getIsFormDataReady()}
                        />
                        <DefaultCloseSubWindowButton
                            text={cancelText()}
                        />
                        {!!meetingConnectionOptions.length && <OptionsButton
                            className='options-button--right-margin-auto'
                            modifier={['fat', 'options']}
                            label={intl.formatMessage(MORE_BUTTON_TEXT)}
                            options={meetingConnectionOptions}
                            position='top'
                            align='start'
                        />}
                    </div>
                </>
            )}
        />
    );
}));

interface IPropsUniqueJoiningLinkForm {
    accountId: number;
    event: IEventDetailsDTO;
    meetingId: number;
    meetingConnectionFormDataVM: MeetingConnectionsAggregateRoot;
    subWindowFormMethods: ISubWindowFormMethods;
    onChangeMeetingConnection: (entity: onChangeConnectionEntity, connectionId: number | null) => void;
    editConnection: (
        meetingId: number,
        connection: IConnectionDetailsDTO,
        onSuccess?: (updatedDetails?: IConnectionDetailsDTO) => void
    ) => void;
    formSubmitAction: (data: IMeetingConnectionsDTO) => Promise<boolean>;
    initData: IMeetingConnectionsDTO;
    updateUniqueJoiningLinkForm: (callback: () => void) => void;
    saveCompletedCallback: () => Promise<void>;
    closeMeetingConnectionPopUp: () => void;
    childConnectionsVM: ChildConnectionsViewModel;
}

const UniqueJoiningLinkModalForm = withViewModel(UniqueJoiningLinkModal, async (props: IPropsUniqueJoiningLinkForm) => {
    const {
        accountId,
        event,
        meetingId,
        meetingConnectionFormDataVM,
        subWindowFormMethods,
        onChangeMeetingConnection,
        editConnection,
        formSubmitAction,
        initData,
        updateUniqueJoiningLinkForm,
        saveCompletedCallback,
        closeMeetingConnectionPopUp,
        childConnectionsVM
    } = props;

    return new UniqueJoiningLinkFormVM(
        accountId,
        event,
        meetingId,
        meetingConnectionFormDataVM,
        subWindowFormMethods,
        onChangeMeetingConnection,
        editConnection,
        formSubmitAction,
        initData,
        updateUniqueJoiningLinkForm,
        saveCompletedCallback,
        closeMeetingConnectionPopUp,
        childConnectionsVM
    );
}, props => [props.accountId, props.event, props.meetingId]);


export { UniqueJoiningLinkModalForm, UniqueJoiningLinkModal };
